import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, LogOut, ArrowLeft, Search, Check, X, AlertTriangle, 
  Users, User, Briefcase, Filter, ChevronDown, Eye, Clock, Mail, Phone
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminPendingApprovals() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    user_type: 'all',
    referred_only: false,
    has_flags: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 25;

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) fetchPendingUsers();
  }, [filters, offset, user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      setUser(response.data);
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchPendingUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.user_type !== 'all') params.append('user_type', filters.user_type);
      if (filters.referred_only) params.append('referred_only', 'true');
      if (filters.has_flags) params.append('has_flags', 'true');
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/pending-approvals?${params.toString()}`,
        { withCredentials: true }
      );
      setPendingUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, autoQualifyReferral = true) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/approve`,
        { auto_qualify_referral: autoQualifyReferral },
        { withCredentials: true }
      );
      toast.success('User approved successfully');
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/reject`,
        { reason },
        { withCredentials: true }
      );
      toast.success('User rejected');
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      navigate('/login');
    }
  };

  if (loading && !pendingUsers.length) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Pending Approvals</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user?.name}</span>
            <button onClick={handleLogout} className="btn-secondary text-sm flex items-center gap-2" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="card bg-orange-50 border-orange-200">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-orange-500" />
              <div>
                <p className="text-2xl font-bold text-slate-900">{total}</p>
                <p className="text-sm text-slate-600">Pending Review</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Filters</h3>
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className="btn-secondary text-sm flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? 'Hide' : 'Show'} Filters
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-200">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">User Type</label>
                <select 
                  value={filters.user_type}
                  onChange={(e) => setFilters({...filters, user_type: e.target.value})}
                  className="w-full"
                >
                  <option value="all">All Types</option>
                  <option value="tradesperson">Tradespeople Only</option>
                  <option value="employer">Employers Only</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={filters.referred_only}
                    onChange={(e) => setFilters({...filters, referred_only: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700">Referred Users Only</span>
                </label>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={filters.has_flags}
                    onChange={(e) => setFilters({...filters, has_flags: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700">Has Suspicious Flags</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Pending Users List */}
        <div className="card">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Pending Users ({total})</h3>

          {pendingUsers.length === 0 ? (
            <div className="text-center py-12">
              <Check className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">No pending approvals</p>
              <p className="text-slate-500 text-sm">All users have been reviewed</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingUsers.map((pendingUser) => (
                <div 
                  key={pendingUser.user_id} 
                  className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 transition-colors"
                  data-testid={`pending-user-${pendingUser.user_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${
                        pendingUser.user_type === 'tradesperson' ? 'bg-orange-100' : 'bg-blue-100'
                      }`}>
                        {pendingUser.user_type === 'tradesperson' ? (
                          <Briefcase className="h-6 w-6 text-orange-600" />
                        ) : (
                          <User className="h-6 w-6 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-slate-900">{pendingUser.name}</h4>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            pendingUser.user_type === 'tradesperson' 
                              ? 'bg-orange-100 text-orange-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {pendingUser.user_type}
                          </span>
                          {pendingUser.suspected_duplicate_phone && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Duplicate Phone
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {pendingUser.email}
                          </span>
                          {pendingUser.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {pendingUser.phone}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          Signed up: {new Date(pendingUser.created_at).toLocaleDateString()}
                        </p>
                        {pendingUser.referrer && (
                          <p className="text-sm text-green-600 mt-1">
                            Referred by: {pendingUser.referrer.name} ({pendingUser.referrer.email})
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => navigate(`/admin/user/${pendingUser.user_id}`)}
                        className="btn-secondary text-sm flex items-center gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </button>
                      <button 
                        onClick={() => handleApprove(pendingUser.user_id)}
                        className="btn-primary text-sm flex items-center gap-1"
                        data-testid={`approve-btn-${pendingUser.user_id}`}
                      >
                        <Check className="h-4 w-4" />
                        Approve
                      </button>
                      <button 
                        onClick={() => handleReject(pendingUser.user_id)}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded text-sm flex items-center gap-1"
                        data-testid={`reject-btn-${pendingUser.user_id}`}
                      >
                        <X className="h-4 w-4" />
                        Reject
                      </button>
                    </div>
                  </div>

                  {/* Profile Preview */}
                  {pendingUser.profile && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-sm font-medium text-slate-700 mb-2">Profile Details:</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {pendingUser.user_type === 'tradesperson' && pendingUser.profile.trade && (
                          <div>
                            <span className="text-slate-500">Trade:</span>
                            <span className="ml-2 font-medium">{pendingUser.profile.trade}</span>
                          </div>
                        )}
                        {pendingUser.profile.work_areas && (
                          <div>
                            <span className="text-slate-500">Areas:</span>
                            <span className="ml-2 font-medium">{pendingUser.profile.work_areas?.join(', ')}</span>
                          </div>
                        )}
                        {pendingUser.profile.daily_rate && (
                          <div>
                            <span className="text-slate-500">Daily Rate:</span>
                            <span className="ml-2 font-medium">£{pendingUser.profile.daily_rate}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button 
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="btn-secondary text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button 
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                  className="btn-secondary text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
